import React from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  StorageOutlined,
  Download,
  Delete,
  DeleteSweep,
  Visibility,
  Analytics,
  Code,
  Timeline,
  CheckCircle,
  Pending,
  Error
} from '@mui/icons-material';

const RepositoryTab = ({
  files,
  loading,
  fileProgress,
  smartProgress,
  handleDownloadFile,
  handleViewAnalysis,
  handleStartSmartAnalysis,
  handleDeleteFile,
  handleDeleteAllFiles,
  loadFiles,
  formatFileSize,
  userPermissions
}) => {
  return (
    <Box sx={{ pt: 4, pb: 4 }}>
      <Card elevation={12} sx={{ borderRadius: 3 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: '#000000', color: '#ffffff' }}>
              <StorageOutlined />
            </Avatar>
          }
          title={
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              TTL Files Repository
            </Typography>
          }
          subheader={`${Array.isArray(files) ? files.length : 0} files in storage`}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
              {userPermissions?.can_delete && Array.isArray(files) && files.length > 0 && (
                <Tooltip title="Delete all files">
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweep />}
                    onClick={handleDeleteAllFiles}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Delete All
                  </Button>
                </Tooltip>
              )}
              <IconButton onClick={loadFiles} disabled={loading}>
                <Timeline sx={{ 
                  animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} />
              </IconButton>
            </Box>
          }
        />
        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)' }} />
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography color="text.secondary">Loading files...</Typography>
            </Box>
          ) : (!Array.isArray(files) || files.length === 0) ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Code sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No TTL files uploaded yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Upload your first Turtle/TTL file to get started
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Filename
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Uploaded By
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Upload Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Size
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                      SMART
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file) => (
                    <TableRow 
                      key={file.id}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'rgba(0, 0, 0, 0.02)' 
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: '#000000', color: '#ffffff', width: 32, height: 32 }}>
                            <Code fontSize="small" />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {file.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.primary">
                          {file.uploaded_by?.first_name || file.uploaded_by?.last_name 
                            ? `${file.uploaded_by.first_name || ''} ${file.uploaded_by.last_name || ''}`.trim()
                            : file.uploaded_by?.username || 'Unknown'}
                        </Typography>
                        {file.uploaded_by?.email && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {file.uploaded_by.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(file.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={formatFileSize(file.file_size)}
                          size="small"
                          sx={{ bgcolor: 'rgba(0, 0, 0, 0.08)', color: 'primary.main' }}
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const progress = fileProgress[file.id];
                          if (progress) {
                            if (progress.status === 'completed') {
                              return (
                                <>
                                  <Chip 
                                    label="Completed"
                                    size="small"
                                    color="success"
                                    icon={<CheckCircle />}
                                  />
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {progress.total_requirements} requirements translated
                                  </Typography>
                                </>
                              );
                            } else if (progress.status === 'pending' && progress.progress_percentage > 0) {
                              return (
                                <>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <CircularProgress size={16} />
                                    <Chip 
                                      label={`Processing ${Math.round(progress.progress_percentage)}%`}
                                      size="small"
                                      color="primary"
                                    />
                                  </Box>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {progress.translated_requirements}/{progress.total_requirements} requirements
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={progress.progress_percentage} 
                                    sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                  />
                                </>
                              );
                            } else if (progress.status === 'pending') {
                              return (
                                <Chip 
                                  label="Pending"
                                  size="small"
                                  color="warning"
                                  icon={<Pending />}
                                />
                              );
                            } else if (progress.status === 'failed') {
                              return (
                                <Chip 
                                  label="Error"
                                  size="small"
                                  color="error"
                                  icon={<Error />}
                                />
                              );
                            }
                          }
                          return (
                            <Chip 
                              label="Pending"
                              size="small"
                              color="warning"
                              icon={<Pending />}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const fileSmartProgress = smartProgress[file.id];
                          if (fileSmartProgress) {
                            if (fileSmartProgress.status === 'completed') {
                              return (
                                <>
                                  <Chip 
                                    label="✓ Analyzed"
                                    size="small"
                                    color="success"
                                    icon={<CheckCircle />}
                                  />
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {fileSmartProgress.analyzed_requirements || fileSmartProgress.total_requirements} requirements analyzed
                                  </Typography>
                                </>
                              );
                            } else if (fileSmartProgress.status === 'analyzing') {
                              return (
                                <>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <CircularProgress size={16} />
                                    <Chip 
                                      label={`Analyzing ${Math.round(fileSmartProgress.progress_percentage || 0)}%`}
                                      size="small"
                                      color="info"
                                    />
                                  </Box>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {fileSmartProgress.analyzed_requirements || 0}/{fileSmartProgress.total_requirements || 0} requirements
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={fileSmartProgress.progress_percentage || 0} 
                                    sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                  />
                                </>
                              );
                            } else if (fileSmartProgress.status === 'pending' && fileSmartProgress.analyzed_requirements > 0) {
                              // Partially analyzed (was interrupted or in pending state with some analysis done)
                              return (
                                <>
                                  <Chip 
                                    label="Partially Analyzed"
                                    size="small"
                                    color="warning"
                                    icon={<Pending />}
                                  />
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {fileSmartProgress.analyzed_requirements}/{fileSmartProgress.total_requirements} requirements
                                  </Typography>
                                </>
                              );
                            } else if (fileSmartProgress.status === 'failed') {
                              return (
                                <Chip 
                                  label="Error"
                                  size="small"
                                  color="error"
                                  icon={<Error />}
                                />
                              );
                            }
                          }
                          // Not analyzed or pending with 0 analyzed
                          return (
                            <Chip 
                              label="Not analyzed"
                              size="small"
                              variant="outlined"
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Download file">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadFile(file)}
                            sx={{ 
                              mr: 1,
                              color: 'primary.main',
                              '&:hover': { 
                                bgcolor: 'rgba(0, 0, 0, 0.08)',
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {file.is_processed && (
                          <>
                            <Tooltip title="View analysis">
                              <IconButton
                                size="small"
                                onClick={() => handleViewAnalysis(file.id)}
                                sx={{ 
                                  mr: 1,
                                  color: '#000000',
                                  '&:hover': { 
                                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {userPermissions?.can_delete && (
                          <Tooltip title="Delete file">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteFile(file.id)}
                              sx={{ 
                                color: 'secondary.main',
                                '&:hover': { 
                                  bgcolor: 'rgba(0, 0, 0, 0.08)',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!Array.isArray(files) || files.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {loading ? 'Loading files...' : 'No TTL files uploaded yet'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RepositoryTab;
